import { z } from 'zod';
import { ConnectorKind } from '../connectors/kind.js';
/**
 * Agent behavior settings — voice / length / stance, autonomy gates,
 * citation policy, and per-connector trust dials. Drives both the
 * Today briefing tone and the agent's autonomous-action threshold.
 *
 * `autonomy.*` flags gate the agent's right to *act* (vs. *suggest*) on
 * the CEO's behalf. `runWarehouseSql` defaults true because read-only
 * SQL against the warehouse is low-risk; `sendReplies` and
 * `pauseCampaigns` default false because they're externally visible.
 *
 * `citations.*` enforces the LeanOpx provenance contract — every
 * AI-surfaced fact carries `<SourceBadge>` + `<ConfidenceMeter>` and
 * INTERP tags distinguish interpretation from grounded fact. Turning
 * any of these off is an explicit CEO decision recorded in the audit
 * trail.
 *
 * `connectorTrust` is the per-connector 0..1 weight the Ask pipeline
 * applies when ranking candidate evidence — lower for hearsay-prone
 * sources (Slack), higher for structured truth (Salesforce, Stripe).
 * Missing keys mean "trust fully" (1.0). The `/admin/connectors/trust`
 * page writes here via `PATCH /orgs/:o/preferences/connector-trust`.
 * Default is the empty record so an unconfigured org's Ask pipeline
 * behaves as if every source were trusted equally.
 */
/** Default trust weight for a connector with no explicit override. */
export const DEFAULT_CONNECTOR_TRUST = 1.0;
/** 0..1 trust weight per connector kind. Missing keys → 1.0 at read-time
 *  (resolved through {@link trustWeightFor}). Zod 4's enum-keyed
 *  `z.record` infers a closed `Record<K, V>` — but our semantics are
 *  "missing key means trust-fully default," so consumers pass sparse
 *  maps. We mirror the `permissions.ts` pattern: keep the schema for
 *  runtime validation (clamps every value to 0..1 + restricts keys to
 *  the closed `ConnectorKind` set) but override the inferred type as
 *  `Partial<Record<K, V>>` so calls like `{ slack: 0.2 }` typecheck.
 *
 *  The default of `{}` is also the schema-level safety net: a read of
 *  a doc that predates this field comes back with an empty map, which
 *  parses cleanly (every key absent = every dial at 1.0).
 */
// Zod 4 distinguishes `z.record` (closed — every enum key required at
// parse time) from `z.partialRecord` (sparse — every enum key optional
// at parse time). Our semantics are sparse-by-design: missing key
// means "trust-fully default," so a partial input must parse cleanly.
// We use `partialRecord` for the runtime contract; the inferred type
// is the same `Partial<Record<K, V>>` shape the consumer code wants,
// no `.transform()` gymnastics needed.
//
// The default-via-thunk gives Zod 4 a fully-populated callable rather
// than the literal `{}` (which Zod 4 would reject as missing keys at
// the inferred type level even though it's the contract we want).
const ConnectorTrustMapSchema = z
    .partialRecord(ConnectorKind, z.number().min(0).max(1))
    .default(() => ({}));
export const ConnectorTrustMap = ConnectorTrustMapSchema;
/**
 * Resolve a per-connector trust weight from a trust map, defaulting to
 * `DEFAULT_CONNECTOR_TRUST` (1.0) when the kind has no explicit entry.
 * Shared so the Ask synthesis weighting and the `/admin/connectors/trust`
 * UI agree on the semantics — "missing key" never means "0", it always
 * means "trust fully."
 */
export function trustWeightFor(map, kind) {
    if (!map)
        return DEFAULT_CONNECTOR_TRUST;
    const v = map[kind];
    if (typeof v !== 'number' || !Number.isFinite(v))
        return DEFAULT_CONNECTOR_TRUST;
    return v;
}
export const AgentVoice = z.enum(['Coach', 'Analyst', 'Operator', 'Quiet']);
/** Lowercase canonical — per-user axis (ui-store). `voicePromptModifier`
 *  normalizes inbound `AgentVoice` (TitleCase) to this form. */
export const AgentVoiceCanonical = z.enum(['coach', 'analyst', 'operator', 'quiet']);
export const AgentLength = z.enum(['Terse', 'Crisp', 'Standard', 'Thorough']);
export const AgentStance = z.enum(['Direct', 'Diplomatic', 'Probing']);
export const AgentAutonomy = z.object({
    draftDocs: z.boolean().default(true),
    scheduleMeetings: z.boolean().default(true),
    sendReplies: z.boolean().default(false),
    pauseCampaigns: z.boolean().default(false),
    runWarehouseSql: z.boolean().default(true),
});
export const AgentCitations = z.object({
    requireSourceCitation: z.boolean().default(true),
    tagInterpretation: z.boolean().default(true),
    showConfidence: z.boolean().default(true),
});
export const AgentSettings = z.object({
    voice: AgentVoice.default('Coach'),
    length: AgentLength.default('Crisp'),
    stance: AgentStance.default('Direct'),
    // `.default({})` on the nested schemas so a forward-compat read of an
    // older doc that only set `voice` (or only `connectorTrust`) still
    // parses — the inner fields have their own defaults, and the missing
    // sub-object falls back here rather than throwing at the parent.
    // Without these, the `writeOrgConnectorTrust` flow (which only writes
    // `agent.connectorTrust`) would write a doc that the next read can't
    // parse, breaking the round-trip.
    autonomy: AgentAutonomy.default(() => AgentAutonomy.parse({})),
    citations: AgentCitations.default(() => AgentCitations.parse({})),
    connectorTrust: ConnectorTrustMap,
});
